import { useMemo } from 'react'

import { useMisc } from '@/components/providers/MiscProvider/MiscProvider'
import { DATEFORMAT_FULL } from '@/defs/date'
import { type IconType, isIconType } from '@/defs/icon'
import { CARDART, type Dict } from '@/defs/misc'
import {
  COLOR_ID_COLORLESS,
  COLOR_ID_DUALS,
  COLOR_ID_FIVE,
  COLOR_ID_LABELS,
  COLOR_ID_MONOS,
  COLOR_ID_QUADS,
  COLOR_ID_TRIS,
  type ColorIdType,
} from '@/defs/mtg'
import { type NavDeviceType, type NavItemType } from '@/defs/nav'
import { type RouteSectionType, type RouteType } from '@/defs/route'
import { type SetType } from '@/defs/set'
import { WORDPRESS_CATEGORY_LABELS, WORDPRESS_CATEGORY_SLUGS_MAIN } from '@/defs/wordpress'
import { getSetIcon } from '@/utils/cardUtil'
import { slugify } from '@/utils/commonUtil'
import { formatDate } from '@/utils/formatUtil'
import { filterNavItems } from '@/utils/navUtil'

const ciHelper = (colorIds: ColorIdType[], section: RouteSectionType) =>
  colorIds.map(
    (ci) =>
      ({
        icons: COLOR_ID_LABELS[ci].colors.map((c) => `mtg-${c}` as IconType),
        id: `nav-${section}-${ci}`,
        label: COLOR_ID_LABELS[ci].label,
        route: { colorId: ci, section },
      }) satisfies NavItemType,
  )

const setHelper = (sets: SetType[], styles: Dict<string>) =>
  sets.map(
    ({ code, icon_svg_uri, name, released_at }) =>
      ({
        iconUrl: getSetIcon(icon_svg_uri),
        id: `nav-sets-${code}`,
        label: (
          <span className={styles.set}>
            {name}
            {released_at && <span className={styles.date}>{formatDate(released_at, DATEFORMAT_FULL)}</span>}
          </span>
        ),
        route: { section: 'sets', setSlug: code },
      }) satisfies NavItemType,
  )

export const useNavItems = (styles: Dict<string>, device: NavDeviceType) => {
  const { articles, guides, precons, sets, tags } = useMisc()

  return useMemo<NavItemType>(() => {
    const today = new Date().toISOString().slice(0, 10)
    const setsUpcoming = sets.filter(({ released_at }) => !!released_at && released_at > today)
    const setsReleased = sets.filter((set) => !setsUpcoming.includes(set))

    const tagsPopular: NavItemType[] = [
      ...tags.map<NavItemType>(({ name, slug }) => ({
        id: `nav-tags-${slug}`,
        label: name,
        route: { section: 'tags', tagSlug: slug },
      })),
      { id: 'nav-tags-more', label: 'More...', route: { section: 'tagsList' } },
    ]
    const tagsAll = { id: 'nav-tags-all', label: 'Tags', route: { section: 'tagsList' } } satisfies NavItemType

    const submenu: NavItemType[] = [
      {
        icon: 'edhrecGraphic',
        id: 'nav-home',
        label: 'Home',
        only: 'mobile',
        route: { section: 'home' },
      },
      {
        backgroundImage: CARDART.PONDER,
        icon: 'rss',
        id: 'nav-articles',
        label: 'Articles',
        submenu: [
          {
            id: 'nav-articles-categories',
            submenu: [
              {
                id: 'nav-articles-categories-all',
                label: 'All Articles',
                route: { section: 'articlesLanding' },
              },
              ...WORDPRESS_CATEGORY_SLUGS_MAIN.map(
                (slug) =>
                  ({
                    id: `nav-articles-categories-${slug}`,
                    label: WORDPRESS_CATEGORY_LABELS[slug].label,
                    route: { articleCategorySlug: slug, section: 'articlesCategory' } satisfies RouteType,
                  }) satisfies NavItemType,
              ),
            ],
          },
          {
            id: 'nav-articles-header',
            label: 'Latest',
            route: { section: 'articlesLanding' },
            size: 'sm',
            submenu: [
              ...articles.slice(0, 3).map(
                (article) =>
                  ({
                    id: `nav-articles-${article.slug}`,
                    label: (
                      <span className={styles.article}>
                        {article.title}
                        <span className={styles.author}>
                          by <span>{article.author}</span>
                        </span>
                      </span>
                    ),
                    route: { articleSlug: article.slug, section: 'article' },
                  }) satisfies NavItemType,
              ),
              { id: 'nav-articles-more', label: 'More...', route: { section: 'articlesLanding' } },
            ],
          },
          {
            id: 'nav-guides',
            label: 'Guides',
            route: { section: 'guidesLanding' },
            size: 'sm',
            submenu: [
              ...guides.slice(0, 3).map<NavItemType>(
                (guide) =>
                  ({
                    id: `nav-guides-${guide.slug}`,
                    label: (
                      <span className={styles.article}>
                        {guide.title}
                        <span className={styles.author}>
                          by <span>{guide.author}</span>
                        </span>
                      </span>
                    ),
                    route: { articleSlug: guide.slug, section: 'guide' },
                  }) satisfies NavItemType,
              ),
              { id: 'nav-guides-more', label: 'More...', route: { section: 'guidesLanding' } },
            ],
          },
        ],
      },
      {
        backgroundImage: CARDART.SOL_RING,
        icon: 'cards',
        id: 'nav-cards',
        label: 'Cards',
        submenu: [
          {
            id: 'nav-cards-categories',
            submenu: [
              { id: 'nav-cards-categories-top', label: 'Top Cards', route: { section: 'top' } },
              { id: 'nav-cards-categories-combos', label: 'Combos', route: { section: 'combos' } },
              { id: 'nav-cards-categories-gcs', label: 'Game Changers', route: { section: 'gameChangers' } },
              { id: 'nav-cards-categories-mana-rocks', label: 'Mana Rocks', route: { section: 'manaRocks' } },
              { id: 'nav-cards-categories-lands', label: 'Landbases', route: { section: 'landsList' } },
              { id: 'nav-cards-categories-salt', label: 'Saltiest Cards', route: { section: 'salt' } },
              { id: 'nav-cards-categories-anned', label: 'Banned Cards', route: { section: 'banned' } },
            ],
          },
          {
            id: 'nav-cards-mono',
            label: 'Mono Color',
            size: 'sm',
            submenu: ciHelper([...COLOR_ID_MONOS, COLOR_ID_COLORLESS], 'top'),
          },
          {
            id: 'nav-cards-two-color',
            label: 'Two Color',
            size: 'sm',
            submenu: ciHelper([...COLOR_ID_DUALS], 'top'),
          },
          {
            id: 'nav-cards-three-color',
            label: 'Three Color',
            size: 'sm',
            submenu: ciHelper([...COLOR_ID_TRIS], 'top'),
          },
          {
            id: 'nav-cards-four-plus-color',
            label: 'Four+ Color',
            size: 'sm',
            submenu: ciHelper([...COLOR_ID_QUADS, COLOR_ID_FIVE], 'top'),
          },
          {
            id: 'nav-cards-types',
            label: 'Type',
            size: 'sm',
            submenu: [
              {
                icon: 'type-creature',
                id: 'nav-cards-types-creatures',
                label: 'Creatures',
                route: { cardtype: 'creatures', section: 'cardtypes' },
              },
              {
                icon: 'type-instant',
                id: 'nav-cards-types-instants',
                label: 'Instants',
                route: { cardtype: 'instants', section: 'cardtypes' },
              },
              {
                icon: 'type-sorcery',
                id: 'nav-cards-types-sorceries',
                label: 'Sorceries',
                route: { cardtype: 'sorceries', section: 'cardtypes' },
              },
              {
                icon: 'type-artifact',
                id: 'nav-cards-types-artifacts',
                label: 'Artifacts',
                route: { cardtype: 'artifacts', section: 'cardtypes' },
                size: 'xs',
              },
              {
                icon: 'type-enchantment',
                id: 'nav-cards-types-enchantments',
                label: 'Enchantments',
                route: { cardtype: 'enchantments', section: 'cardtypes' },
                size: 'xs',
              },
              {
                icon: 'type-battle',
                id: 'nav-cards-types-battles',
                label: 'Battles',
                route: { cardtype: 'battles', section: 'cardtypes' },
              },
              {
                icon: 'type-planeswalker',
                id: 'nav-cards-types-planeswalkers',
                label: 'Planeswalkers',
                route: { cardtype: 'planeswalkers', section: 'cardtypes' },
              },
              {
                icon: 'type-land',
                id: 'nav-cards-types-lands',
                label: 'Lands',
                route: { cardtype: 'lands', section: 'cardtypes' },
                size: 'xs',
              },
            ],
          },
        ],
      },
      {
        backgroundImage: CARDART.ATRAXA_PRAETORS_VOICE_2XM,
        icon: 'crown',
        id: 'nav-commanders',
        label: 'Commanders',
        submenu: [
          {
            id: 'nav-commanders-categories',
            submenu: [
              {
                id: 'nav-commanders-categories-top',
                label: 'Top Commanders',
                route: { section: 'commandersList' },
              },
              { id: 'nav-commanders-categories-partners', label: 'Partners', route: { section: 'partnersList' } },
              { id: 'nav-commanders-categories-vehicles', label: 'Vehicles', route: { section: 'vehicles' } },
            ],
          },
          {
            id: 'nav-commanders-mono',
            label: 'Mono Color',
            size: 'sm',
            submenu: ciHelper([...COLOR_ID_MONOS, COLOR_ID_COLORLESS], 'commandersList'),
          },
          {
            id: 'nav-commanders-two-color',
            label: 'Two Color',
            size: 'sm',
            submenu: ciHelper([...COLOR_ID_DUALS], 'commandersList'),
          },
          {
            id: 'nav-commanders-three-color',
            label: 'Three Color',
            size: 'sm',
            submenu: ciHelper([...COLOR_ID_TRIS], 'commandersList'),
          },
          {
            id: 'nav-commanders-four-plus-color',
            label: 'Four+ Color',
            size: 'sm',
            submenu: ciHelper([...COLOR_ID_QUADS, COLOR_ID_FIVE], 'commandersList'),
          },
        ],
      },
      {
        backgroundImage: CARDART.KAALIA,
        icon: 'cube',
        id: 'nav-precons',
        label: 'Precons',
        submenu: [
          {
            id: 'nav-precons-header',
            label: 'Precons',
            route: { section: 'preconsList' },
            size: 'sm',
            submenu: [
              ...precons.map<NavItemType>((precon) => ({
                hash: slugify(precon.name),
                id: `nav-precons-${precon.name}`,
                label: precon.name,
                route: { section: 'preconsList' },
                size: 'xs',
                submenu: precon.precons.map<NavItemType>(({ colors, name, slug }) => ({
                  icons: colors
                    .split('')
                    .map((c) => `mtg-${c.toLowerCase()}`)
                    .filter((c): c is IconType => isIconType(c)),
                  id: `nav-precons-${slug}`,
                  label: name,
                  route: { preconSlug: slug, section: 'preconsFeatured' },
                })),
              })),
              {
                id: 'nav-precons-more',
                label: 'More...',
                route: { section: 'preconsList' },
              },
            ],
          },
        ],
      },
      {
        backgroundImage: CARDART.LAND,
        icon: 'layerGroup',
        id: 'nav-sets',
        label: 'Sets',
        submenu: [
          {
            id: 'nav-sets-header',
            label: 'Sets',
            route: { section: 'setsList' },
            size: 'sm',
            submenu: [
              { id: 'nav-sets-upcoming', label: 'Upcoming', only: 'mobile' } as const,
              ...setHelper(setsUpcoming, styles),
              ...(setsUpcoming.length && setsReleased.length
                ? [
                    { id: 'nav-sets-released', label: 'New', only: 'mobile' } as const,
                    { id: 'nav-sets-divider', only: 'desktop', variant: 'divider' } as const,
                  ]
                : []),
              ...setHelper(setsReleased, styles),
              { id: 'nav-sets-more', label: 'More...', route: { section: 'setsList' } },
            ],
          },
        ],
      },
      {
        backgroundImage: CARDART.PARALLEL_LIVES,
        icon: 'tag',
        id: 'nav-tags',
        label: 'Tags',
        submenu: [
          {
            id: 'nav-tags-header',
            submenu: [
              { ...tagsAll, only: 'desktop' },
              { ...tagsAll, only: 'mobile', size: 'sm', submenu: tagsPopular },
              {
                id: 'nav-tags-themes',
                label: 'Themes',
                route: { section: 'tags', tagSlug: 'themes' },
              },
              { id: 'nav-tags-typal', label: 'Typal', route: { section: 'tags', tagSlug: 'typal' } },
              {
                id: 'nav-cards-categories-companions',
                label: 'Companions',
                route: { section: 'companions' },
              },
            ],
          },
          { id: 'nav-tags-popular', only: 'desktop', size: 'sm', submenu: tagsPopular },
        ],
      },
      {
        icon: 'chartSimple',
        id: 'nav-recs',
        label: 'Recs',
        route: { section: 'recs' },
      },
    ]

    return { id: 'nav', submenu: filterNavItems(submenu, device) }
  }, [articles, guides, precons, sets, styles, device, tags])
}
